import { RefreshOutlined } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import NestingRunningIcon from '_icons/processing/nesting_ani.svg?react'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { WorkingStepButton } from 'features/BillOfMaterials/components/BomItemWorkingStepSummary'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { uniq } from 'lodash'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PriceScope,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import { useBomItemDetailsDrawerStyles } from '../BomItemDetailsDrawer.styles'
import PartTypeFinancialGraph from '../Graph/PartTypeFinancialGraph/PartTypeFinancialGraph'
import { PriceDetailsForm } from '../PriceDetails/PriceDetailsForm'
import { useFinancialDetails } from '../useFinancialDetails'
import { BomItemDetailsProps } from './ProjectDetails/BomItemDetailsProps'

type Props = {
  containerClassName?: string
} & Omit<BomItemDetailsProps, 'initialFilesToUpload'>

export const BomItemPriceContainer = (props: Props) => {
  const { classes: sharedClasses, cx } = useBomItemDetailsDrawerStyles()
  const { t } = useTranslation()

  const isProjectNesting = useAppSelector(
    projectSelectors.projectIsNestingSelector,
  )

  const [
    removeZeroEstimatedProductionTimeFilter,
    setRemoveZeroEstimatedProductionTimeFilter,
  ] = useState(false)

  const {
    loading,
    priceDetails,
    priceScope,
    originalPriceDetails,
    handleChangeFinancialView,
    handleResetProductionTime,
    handleResetAdditionalCosts,
    handleSaveProductionTime,
    handleSaveAdditionalCosts,
    getItemPriceDetails,
    bomItemTotalProjectQuantity,
    filteringZeroEstimatedProductionTime,
  } = useFinancialDetails({
    bomItemPointer: props.bomItemPointer,
    open: true,
    filterZeroEstimatedProductionTime:
      removeZeroEstimatedProductionTimeFilter === true
        ? false
        : props.filterZeroEstimatedProductionTime,
  })

  const handleReload = useCallback(() => {
    try {
      getItemPriceDetails.cancel()
      getItemPriceDetails(props.bomItemPointer)
      getItemPriceDetails.flush()
    } catch (err) {
      ShowException('BomItemPriceContainer', err)
    }
  }, [getItemPriceDetails, props.bomItemPointer])

  const isPriceDetailsEmpty = () => {
    if (priceDetails?.length > 0) {
      return false
    }

    if (props.filterZeroEstimatedProductionTime) {
      return !priceDetails?.length
    } else {
      return (
        !priceDetails?.length ||
        priceDetails?.findIndex((x) => x.costPrice?.value > 0) === -1
      )
    }
  }

  const isLoadingPriceDetails =
    loading[`get-price-details-${props.bomItemPointer.id}`]

  return (
    <>
      <LoadingContainer
        style={{ height: '60vh' }}
        loading={isPriceDetailsEmpty() && isLoadingPriceDetails}
        noMessage
        empty={
          isPriceDetailsEmpty() &&
          !isLoadingPriceDetails &&
          !isProjectNesting ? (
            <>
              <LocalizedTypography
                translationKey={'project:prices-not-calculated'}
                color="GrayText"
              >
                prices are not calculated for this item. Check the item material
                and working steps
              </LocalizedTypography>
              <IconButton onClick={handleReload} size="small">
                {isLoadingPriceDetails ? (
                  <CircularProgress size={20} />
                ) : (
                  <RefreshOutlined />
                )}
              </IconButton>
            </>
          ) : null
        }
        emptyBox={{
          sx: { height: '100%' },
        }}
      >
        <div style={{ height: '100%' }}>
          {isPriceDetailsEmpty() ? null : (
            <>
              {isProjectNesting && (
                <Tooltip
                  title={t(
                    'project:project-is-nesting-prices-may-change',
                    'project is nesting, prices may change',
                  )}
                >
                  <div
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <NestingRunningIcon width="64px" />
                  </div>
                </Tooltip>
              )}
              <div className={sharedClasses.actions}>
                <div
                  style={{
                    display: 'flex',
                    gap: '1em',
                  }}
                >
                  {uniq(
                    priceDetails?.flatMap((p) => {
                      return p.children?.flatMap(
                        (c) => c.workingStep.secondaryWorkingStep,
                      )
                    }),
                  )
                    .filter((x) => {
                      if (
                        !x ||
                        x === WorkingStepType.Loading ||
                        x === WorkingStepType.Setup ||
                        x === WorkingStepType.Unloading
                      ) {
                        return false
                      }
                      return true
                    })

                    .map((ws) => {
                      return (
                        <WorkingStepButton
                          key={ws}
                          ws={{
                            primaryWorkingStep: ws,
                            count: 1,
                          }}
                          isNotAllowed={false}
                          shouldFilter
                        />
                      )
                    })}
                </div>
                <TextField
                  select
                  value={priceScope}
                  label="scope"
                  slotProps={{
                    select: {
                      onChange: (ev) =>
                        handleChangeFinancialView(
                          originalPriceDetails,
                          ev.target.value as PriceScope,
                        ),
                      renderValue: (value) => (
                        <Typography variant="body2" color="textPrimary">
                          {value === PriceScope.Unit
                            ? t('common:perUnit', 'per unit')
                            : t('common:total') +
                              ` (x${bomItemTotalProjectQuantity})`}
                        </Typography>
                      ),
                    },
                  }}
                >
                  <MenuItem value={PriceScope.Unit}>
                    {t('common:perUnit', 'per unit')}
                  </MenuItem>
                  <MenuItem value={PriceScope.Total}>
                    {t('common:total') + ` (x${bomItemTotalProjectQuantity})`}
                  </MenuItem>
                </TextField>
                <IconButton
                  onClick={handleReload}
                  size="small"
                  style={{ width: '32px', height: '32px' }}
                >
                  {isLoadingPriceDetails ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshOutlined />
                  )}
                </IconButton>
              </div>
              <div className={props.containerClassName}>
                {!props.disableGraph && (
                  <div className={cx(sharedClasses.graph, 'graph')}>
                    {(!filteringZeroEstimatedProductionTime ||
                      removeZeroEstimatedProductionTimeFilter) && (
                      <div style={{ width: '100%' }}>
                        <PartTypeFinancialGraph
                          bomItemPointer={props.bomItemPointer}
                          priceDetails={priceDetails}
                          priceScope={priceScope}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className={'prices-details-form'}>
                  <PriceDetailsForm
                    bomItemPointer={props.bomItemPointer}
                    priceDetails={priceDetails}
                    priceScope={priceScope}
                    handleResetProductionTime={handleResetProductionTime}
                    handleResetAdditionalCosts={handleResetAdditionalCosts}
                    handleSaveAdditionalCostPrice={handleSaveAdditionalCosts}
                    handleSaveProductionTime={handleSaveProductionTime}
                    loading={loading}
                  />
                </div>
                {filteringZeroEstimatedProductionTime &&
                  !removeZeroEstimatedProductionTimeFilter && (
                    <div style={{ textAlign: 'right' }}>
                      <Button
                        onClick={() => {
                          setRemoveZeroEstimatedProductionTimeFilter(true)
                        }}
                        color="error"
                      >
                        filtering activities with zero estimated production time
                      </Button>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      </LoadingContainer>
    </>
  )
}
